import { dirname } from 'path';
import fs from 'fs-extra';
import { getConfigFilePath, getApiKeyFilePath } from './license-paths.js';

const DEFAULT_LICENSE_URL = 'https://license.atlashub.ch';
const REQUEST_TIMEOUT_MS = 10_000;

// ── Response types ──────────────────────────────────────────────────────────

export interface TrialResponse {
  clientId: string;
  licenses: Array<{
    licenseId: string;
    product: string;
    token: string;
  }>;
  expiresAt: string;
}

export interface ActivationResponse {
  activated: boolean;
  activatedAt?: string;
  heartbeatIntervalHours: number;
  gracePeriodDays: number;
}

export interface HeartbeatResponse {
  valid: boolean;
  revoked: boolean;
  reason: string | null;
  updatedLimits: Record<string, number> | null;
  newJwtToken: string | null;
}

export interface ValidateFeatureScope {
  application_id: string | null;
  application_code: string;
  modules: Array<{ module_id: string; module_code: string }>;
}

export interface ValidateResponse {
  valid: boolean;
  revoked: boolean;
  product: string;
  clientId: string;
  clientName: string;
  clientStatus: string;
  environment: string;
  features: ValidateFeatureScope[];
  expiresAt: string;
  maxInstances: number;
  maxUsers: number;
  gracePeriodDays: number;
  renewalDate: string | null;
  isPerpetual: boolean;
}

export interface LicenseApiError {
  status: number;
  message: string;
  code?: string;
}

// ── URL resolution ──────────────────────────────────────────────────────────

/**
 * Resolve the license server URL.
 * Priority: env var > config file > default
 */
export async function getLicenseServerUrl(): Promise<string> {
  // 1. Environment variable
  const envUrl = process.env.SMARTSTACK_LICENSE_URL;
  if (envUrl) return envUrl.replace(/\/+$/, '');

  // 2. Config file
  try {
    const configPath = getConfigFilePath();
    if (await fs.pathExists(configPath)) {
      const config = await fs.readJSON(configPath);
      if (config?.licenseServerUrl) return config.licenseServerUrl.replace(/\/+$/, '');
    }
  } catch {
    // Ignore — fall through to default
  }

  // 3. Default
  return DEFAULT_LICENSE_URL;
}

/**
 * Load API key from env var or file.
 */
export async function getApiKey(): Promise<string | null> {
  const envKey = process.env.SMARTSTACK_API_KEY;
  if (envKey) return envKey.trim();

  try {
    const keyPath = getApiKeyFilePath();
    if (await fs.pathExists(keyPath)) {
      return (await fs.readFile(keyPath, 'utf-8')).trim();
    }
  } catch {
    // Ignore
  }

  return null;
}

/**
 * Save API key to file.
 */
export async function saveApiKey(apiKey: string): Promise<void> {
  const keyPath = getApiKeyFilePath();
  await fs.ensureDir(dirname(keyPath));
  await fs.writeFile(keyPath, apiKey.trim(), 'utf-8');
}

/**
 * Remove stored API key.
 */
export async function removeApiKey(): Promise<void> {
  const keyPath = getApiKeyFilePath();
  if (await fs.pathExists(keyPath)) {
    await fs.remove(keyPath);
  }
}

// ── HTTP helpers ────────────────────────────────────────────────────────────

function createAbortSignal(): AbortSignal {
  return AbortSignal.timeout(REQUEST_TIMEOUT_MS);
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (response.ok) {
    return (await response.json()) as T;
  }

  let message: string;
  try {
    const body = await response.json() as { message?: string; detail?: string };
    message = body.message || body.detail || response.statusText;
  } catch {
    message = response.statusText;
  }

  const error: LicenseApiError = { status: response.status, message };

  switch (response.status) {
    case 404:
      error.message = `License not found: ${message}`;
      break;
    case 409:
      error.message = `Conflict: ${message}`;
      break;
    case 410:
      error.message = `License revoked or expired: ${message}`;
      break;
    case 422:
      error.message = `Unprocessable entity: ${message}`;
      break;
    case 429:
      error.message = `Rate limited: ${message}`;
      break;
  }

  throw error;
}

// ── LicenseClient ───────────────────────────────────────────────────────────

export class LicenseClient {
  constructor(private readonly baseUrl: string) {}

  /**
   * GET /api/v1/public/signing-key — fetch the RSA public key (PEM)
   */
  async fetchSigningKey(): Promise<string> {
    const response = await fetch(`${this.baseUrl}/api/v1/public/signing-key`, {
      signal: createAbortSignal(),
    });
    if (!response.ok) {
      throw { status: response.status, message: 'Failed to fetch signing key' } as LicenseApiError;
    }
    // Server returns JSON { publicKey: "-----BEGIN PUBLIC KEY-----\n..." }
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const body = (await response.json()) as { publicKey: string };
      return body.publicKey;
    }
    return response.text();
  }

  /**
   * GET /api/v1/public/health — check server availability
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/public/health`, {
        signal: createAbortSignal(),
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * POST /api/v1/public/trial — self-service trial registration
   */
  async registerTrial(data: {
    fullName: string;
    email: string;
    company: string;
    teamSize: string;
  }): Promise<TrialResponse> {
    const response = await fetch(`${this.baseUrl}/api/v1/public/trial`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      signal: createAbortSignal(),
    });
    return handleResponse<TrialResponse>(response);
  }

  /**
   * POST /api/v1/machine/activate — activate a machine (Bearer JWT auth)
   */
  async activate(
    jwt: string,
    data: { machineFingerprint: string; machineName: string; instanceId: string },
  ): Promise<ActivationResponse> {
    const response = await fetch(`${this.baseUrl}/api/v1/machine/activate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify(data),
      signal: createAbortSignal(),
    });
    return handleResponse<ActivationResponse>(response);
  }

  /**
   * POST /api/v1/machine/heartbeat — send heartbeat (Bearer JWT auth)
   */
  async heartbeat(
    jwt: string,
    data: { machineFingerprint: string; instanceId: string },
  ): Promise<HeartbeatResponse> {
    const response = await fetch(`${this.baseUrl}/api/v1/machine/heartbeat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify(data),
      signal: createAbortSignal(),
    });
    return handleResponse<HeartbeatResponse>(response);
  }

  /**
   * GET /api/v1/machine/validate — validate license status (Bearer JWT auth)
   */
  async validate(jwt: string): Promise<ValidateResponse> {
    const response = await fetch(`${this.baseUrl}/api/v1/machine/validate`, {
      headers: { Authorization: `Bearer ${jwt}` },
      signal: createAbortSignal(),
    });
    return handleResponse<ValidateResponse>(response);
  }

  // ── API Key variants (for CI/CD) ───────────────────────────────────────

  /**
   * POST /api/v1/machine/activate/key — activate via API key
   */
  async activateWithApiKey(
    apiKey: string,
    licenseJwt: string,
    data: { machineFingerprint: string; machineName: string; instanceId: string },
  ): Promise<ActivationResponse> {
    const response = await fetch(`${this.baseUrl}/api/v1/machine/activate/key`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Api-Key': apiKey,
      },
      body: JSON.stringify({ licenseJwt, ...data }),
      signal: createAbortSignal(),
    });
    return handleResponse<ActivationResponse>(response);
  }

  /**
   * POST /api/v1/machine/heartbeat/key — heartbeat via API key
   */
  async heartbeatWithApiKey(
    apiKey: string,
    licenseJwt: string,
    data: { machineFingerprint: string; instanceId: string },
  ): Promise<HeartbeatResponse> {
    const response = await fetch(`${this.baseUrl}/api/v1/machine/heartbeat/key`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Api-Key': apiKey,
      },
      body: JSON.stringify({ licenseJwt, ...data }),
      signal: createAbortSignal(),
    });
    return handleResponse<HeartbeatResponse>(response);
  }
}
